import { useEffect, useState } from 'react'

import { useUpdateTrayItem } from 'lib/apollo/hooks'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import { useSPDScan } from 'views/SPDLayout/SPD/Scan/Scan.context'

import { TrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'
import { TrayStatus, UseEditTrayModalProps } from './EditTrayModal.types'
import { BetterIDTrayScrew, TrayPlate } from 'views/SPDLayout/SPD/SPD.types'
import { ItemResponse, Surgery } from 'common/types'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { useUser } from 'app/User'
import { useTrayManagementContext } from 'views/TrayManagementLayout/TrayManagement/TrayManagement.context'

/**
 * Custom hook to handle the state and logic for editing a tray modal.
 *
 * @param {UseEditTrayModalProps} props - The props for the hook.
 * @param {ItemResponse} props.trayData - The data of the tray to be edited.
 * @param {BetterIDTrayScrew[]} [props.trayScrews] - The screws associated with the tray.
 * @returns {{
 *   traySurgeryId: string;
 *   setTraySurgeryId: React.Dispatch<React.SetStateAction<string>>;
 *   handleTraySurgeryIdChange: (id: string) => void;
 *   handleUpdateTrayItem: () => Promise<void>;
 *   updating: boolean;
 *   success: boolean;
 *   setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
 *   failed: boolean;
 *   trayStatus: TrayStatus | undefined;
 *   setTrayStatus: React.Dispatch<React.SetStateAction<TrayStatus | undefined>>;
 *   handleSurgeryChange: (surgery: Surgery) => void;
 * }} The state and functions for editing the tray item's data from our iservices database.
 */
const useEditTrayModal = ({
  trayData,
  trayScrews = [],
}: UseEditTrayModalProps) => {
  const { editTrayItem } = useUpdateTrayItem()
  const {
    trayScrews: spdTrayScrews,
    setTrayData,
    trayPlates,
    setTrayPlates,
  } = useSPD()
  const { user } = useUser()
  const { setProductData } = useSPDScan()
  const { setTrays } = useTrayManagementContext()

  const trayDetails = extractTrayJSONData(trayData)
  const initialSurgeryId = trayDetails?.surgeryId || ''
  const [traySurgeryId, setTraySurgeryId] = useState<string>(initialSurgeryId)
  const [success, setSuccess] = useState<boolean>(false)
  const [failed, setFailed] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const [trayStatus, setTrayStatus] = useState<TrayStatus>(undefined)
  const [selectedTraySurgery, setSelectedTraySurgery] = useState<
    Surgery | undefined
  >(undefined)

  useEffect(() => {
    handleUpdateTrayPlates(trayDetails?.plates)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trayDetails?.plates?.length])

  /**
   * Builds the new tray details.
   *
   * @param {TrayItemProductDetails} previousDetails - The previous tray details.
   * @param {BetterIDTrayScrew[]} screws - The screws associated with the tray.
   * @param {TrayPlate[]} plates - The plates associated with the tray.
   * @param {string} surgeryId - The ID of the surgery.
   * @param {Surgery | undefined} surgery - The details of the surgery.
   * @param {TrayStatus | undefined} status - The status of the tray.
   * @returns {TrayItemProductDetails} The new tray details.
   */
  const buildTrayDetails = (
    previousDetails: TrayItemProductDetails,
    screws: BetterIDTrayScrew[],
    plates: TrayPlate[],
    surgeryId: string,
    surgery: Surgery | undefined,
    status: TrayStatus | undefined
  ): TrayItemProductDetails => ({
    ...previousDetails,
    statusDetails: {
      ...previousDetails.statusDetails,
      lastUpdatedBy: user?.email,
      lastUpdatedAt: new Date().toISOString(),
    },
    surgeryId,
    screws,
    trayImage: previousDetails.trayImage || '',
    trayCategory: previousDetails.trayCategory || '',
    status,
    surgeryDetails: surgery,
    trayType: previousDetails.trayType,
    plates,
  })

  /**
   * Handles updating the tray item.
   */
  const handleUpdateTrayItem = async (
    surgeryId?: string,
    surgery?: Surgery | null
  ) => {
    const trayItem = trayData
    setUpdating(true)

    if (!trayItem || updating) {
      return
    }

    try {
      const trayDetails = extractTrayJSONData(trayItem)

      if (!trayDetails) {
        throw new Error('No tray details were found for this tray.')
      }

      const newProductDetails: TrayItemProductDetails = {
        ...trayDetails,
        surgeryId:
          surgeryId || selectedTraySurgery?._id || trayDetails.surgeryId,
        surgeryDetails:
          surgery || selectedTraySurgery || trayDetails.surgeryDetails,
        screws: spdTrayScrews,
        plates: trayPlates,
        statusDetails: {
          ...trayDetails.statusDetails,
          lastUpdatedBy: user?.email,
          lastUpdatedAt: new Date().toISOString(),
        },
        status:
          surgeryId || selectedTraySurgery?._id
            ? 'assigned'
            : trayDetails.status,
      }

      const { subItems, ...newTrayItem } = trayItem

      const editResult = await editTrayItem({
        ...newTrayItem,
        productDetails: JSON.stringify(newProductDetails),
      })

      if (!editResult || !editResult.editTrayItem) {
        setFailed(true)
        throw new Error('Failed to edit tray item.')
      }

      setSuccess(true)
      setTrayStatus(undefined)
      setTrays((prevTrays: ItemResponse[]) => {
        const updatedTrays = prevTrays.map((tray) =>
          tray.id === trayItem.id
            ? {
                ...tray,
                productDetails: JSON.stringify(newProductDetails),
              }
            : tray
        )
        return updatedTrays
      })

      if (!surgeryId) {
        setProductData([])
        setTrayData(null)
      }

      return true
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Handles changing the tray surgery ID.
   *
   * @param {string} id - The new surgery ID.
   */
  const handleTraySurgeryIdChange = (id: string) => {
    setTraySurgeryId((prevId) => (id === prevId ? '' : id))
  }

  /**
   * Handles changing the surgery.
   *
   * @param {Surgery} surgery - The new surgery.
   */
  const handleSurgeryChange = (surgery: Surgery) => {
    setTrayStatus('assigned')
    setSelectedTraySurgery(surgery)
    handleTraySurgeryIdChange(surgery._id)
  }

  /**
   * Handles updating the state of the trayPlates based on the tray details.
   *
   * @param {TrayPlate[]} plates The plates array in the tray details.
   */
  const handleUpdateTrayPlates = (plates: TrayPlate[] | undefined) => {
    if (plates) {
      setTrayPlates(plates)
    } else {
      setTrayPlates([])
    }
  }

  return {
    traySurgeryId,
    setTraySurgeryId,
    handleTraySurgeryIdChange,
    handleUpdateTrayItem,
    updating,
    success,
    setSuccess,
    failed,
    trayStatus,
    setTrayStatus,
    handleSurgeryChange,
  }
}

export default useEditTrayModal
